import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setActiveTab('overview');
      setCurrentImageIndex(0);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Eye' },
    { id: 'technical', label: 'Technical Details', icon: 'Code' },
    { id: 'gallery', label: 'Gallery', icon: 'Image' },
    { id: 'testimonials', label: 'Testimonials', icon: 'MessageSquare' }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === project.gallery.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? project.gallery.length - 1 : prev - 1
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Live': return 'text-success bg-success/10 border-success/20';
      case 'In Development': return 'text-warning bg-warning/10 border-warning/20';
      case 'Completed': return 'text-primary bg-primary/10 border-primary/20';
      default: return 'text-text-muted bg-muted border-border';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-surface/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Folder" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-primary">{project.title}</h2>
              <div className="flex items-center space-x-3 mt-1">
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
                <span className="text-sm text-text-muted">{project.year}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {project.liveUrl && (
              <Button
                variant="default"
                size="sm"
                iconName="ExternalLink"
                iconPosition="left"
                onClick={() => window.open(project.liveUrl, '_blank')}
              >
                Live Demo
              </Button>
            )}
            {project.githubUrl && (
              <Button
                variant="outline"
                size="sm"
                iconName="Github"
                iconPosition="left"
                onClick={() => window.open(project.githubUrl, '_blank')}
              >
                Code
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
              className="text-text-muted hover:text-text-primary"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border bg-surface/30">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium transition-premium ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Project Image */}
              <div className="relative h-64 rounded-xl overflow-hidden bg-muted">
                <Image
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-3">Project Description</h3>
                <p className="text-text-secondary leading-relaxed">{project.fullDescription}</p>
              </div>

              {/* Key Features */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-3">Key Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {project.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-surface rounded-lg">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Metrics */}
              {project.metrics && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-3">Project Impact</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {project.metrics.map((metric, index) => (
                      <div key={index} className="text-center p-4 bg-surface rounded-lg">
                        <div className="text-2xl font-bold text-primary mb-1">{metric.value}</div>
                        <div className="text-sm text-text-muted">{metric.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'technical' && (
            <div className="space-y-6">
              {/* Tech Stack */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-3">Technology Stack</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {project.techStack.map((tech, index) => (
                    <div key={index} className="flex items-center space-x-2 p-3 bg-surface rounded-lg">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="Code" size={16} className="text-primary" />
                      </div>
                      <span className="text-sm font-medium text-text-primary">{tech}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Architecture */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-3">Architecture Overview</h3>
                <div className="p-4 bg-surface rounded-lg">
                  <p className="text-text-secondary leading-relaxed">{project.architecture}</p>
                </div>
              </div>

              {/* Challenges & Solutions */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-3">Challenges & Solutions</h3>
                <div className="space-y-4">
                  {project.challenges.map((challenge, index) => (
                    <div key={index} className="p-4 bg-surface rounded-lg">
                      <h4 className="font-medium text-text-primary mb-2 flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={16} className="text-warning" />
                        <span>Challenge:</span>
                      </h4>
                      <p className="text-text-secondary mb-3">{challenge.problem}</p>
                      <h4 className="font-medium text-text-primary mb-2 flex items-center space-x-2">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                        <span>Solution:</span>
                      </h4>
                      <p className="text-text-secondary">{challenge.solution}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div className="space-y-6">
              {/* Main Image Display */}
              <div className="relative">
                <div className="relative h-96 rounded-xl overflow-hidden bg-muted">
                  <Image
                    src={project.gallery[currentImageIndex]}
                    alt={`${project.title} screenshot ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {project.gallery.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-premium"
                      >
                        <Icon name="ChevronLeft" size={20} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-premium"
                      >
                        <Icon name="ChevronRight" size={20} />
                      </button>
                    </>
                  )}
                </div>
                
                {project.gallery.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {project.gallery.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-premium ${
                          index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Thumbnail Grid */}
              {project.gallery.length > 1 && (
                <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                  {project.gallery.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative h-20 rounded-lg overflow-hidden transition-premium ${
                        index === currentImageIndex ? 'ring-2 ring-primary' : 'hover:opacity-80'
                      }`}
                    >
                      <Image
                        src={image}
                        alt={`${project.title} thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'testimonials' && (
            <div className="space-y-6">
              {project.testimonials.map((testimonial, index) => (
                <div key={index} className="p-6 bg-surface rounded-xl">
                  <div className="flex items-start space-x-4">
                    <Image
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-semibold text-text-primary">{testimonial.name}</h4>
                        <span className="text-sm text-text-muted">•</span>
                        <span className="text-sm text-text-muted">{testimonial.role}</span>
                      </div>
                      <p className="text-text-secondary leading-relaxed mb-3">"{testimonial.content}"</p>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={14}
                            className={`${
                              i < testimonial.rating ? 'text-warning fill-current' : 'text-border'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;